// ABOUTME: Admin webhook log interface providing comprehensive webhook event viewing and manual retry management
// ABOUTME: Features filtering, pagination, expandable payload viewing, and retry functionality for failed webhooks

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Link,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  ExpandMore,
  ExpandLess,
  Replay
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import axios from 'axios';
import SharedAppBar from './SharedAppBar';
import { getAdminWebhooks, retryWebhook } from '../services/api';

interface WebhookEvent {
  id: number;
  eventType: string;
  lemonSqueezyEventId: string | null;
  familyId: number | null;
  familyName: string | null;
  subscriptionId: number | null;
  payload: string;
  status: string;
  errorMessage: string | null;
  retryCount: number;
  processedAt: string | null;
  createdAt: string;
}

interface WebhookListResponse {
  data: WebhookEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payload-tabpanel-${index}`}
      aria-labelledby={`payload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EVENT_TYPES = [
  'subscription_created',
  'subscription_payment_success',
  'subscription_payment_failed',
  'subscription_cancelled',
  'subscription_updated',
  'order_refunded'
];

const STATUSES = ['success', 'failed', 'retrying'];

const AdminWebhookLog: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Filters
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [familyId, setFamilyId] = useState<string>('');

  // Retry state
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryingWebhook, setRetryingWebhook] = useState<WebhookEvent | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: NonNullable<Parameters<typeof getAdminWebhooks>[0]> = {
        page: page + 1,
        pageSize: pageSize
      };

      if (selectedEventTypes.length > 0) {
        params.eventTypes = selectedEventTypes;
      }

      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses;
      }

      if (fromDate) {
        params.fromDate = fromDate.toISOString();
      }

      if (toDate) {
        params.toDate = toDate.toISOString();
      }

      if (familyId) {
        params.familyId = parseInt(familyId);
      }

      const response: WebhookListResponse = await getAdminWebhooks(params);
      setWebhooks(response.data);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('Error fetching webhooks:', err);
      setError('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, selectedEventTypes, selectedStatuses, fromDate, toDate, familyId]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleNavigateHome = () => {
    navigate('/admin');
  };

  const handleRefresh = () => {
    fetchWebhooks();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpandRow = (webhookId: number) => {
    if (expandedRow === webhookId) {
      setExpandedRow(null);
      setTabValue(0);
    } else {
      setExpandedRow(webhookId);
      setTabValue(0);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEventTypeToggle = (eventType: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType]
    );
    setPage(0);
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setPage(0);
  };

  const handleRetryClick = (webhook: WebhookEvent) => {
    setRetryingWebhook(webhook);
    setRetryDialogOpen(true);
  };

  const handleRetryCancel = () => {
    setRetryDialogOpen(false);
    setRetryingWebhook(null);
  };

  const handleRetryConfirm = async () => {
    if (!retryingWebhook) return;

    try {
      setRetrying(true);
      const result = await retryWebhook(retryingWebhook.id);

      setSnackbarMessage(result.message || 'Webhook retried successfully');
      setSnackbarSeverity(result.success ? 'success' : 'error');
      setSnackbarOpen(true);

      // Refresh the webhook list
      await fetchWebhooks();
    } catch (err) {
      console.error('Error retrying webhook:', err);
      const responseError = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      const message = err instanceof Error ? err.message : String(err);
      setSnackbarMessage(responseError || message || 'Failed to retry webhook');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setRetrying(false);
      setRetryDialogOpen(false);
      setRetryingWebhook(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'retrying':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const parsePayload = (payload: string) => {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  };

  const renderFormattedPayload = (payload: string) => {
    const parsed = parsePayload(payload);
    if (!parsed) return <Typography>Invalid JSON</Typography>;

    const meta = parsed.meta || {};
    const data = parsed.data || {};
    const attributes = data.attributes || {};

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Event ID:</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{meta.event_id || 'N/A'}</Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Event Name:</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{meta.event_name || 'N/A'}</Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom Data:</Typography>
        <Typography variant="body2" component="pre" sx={{ mb: 2 }}>
          {JSON.stringify(meta.custom_data || {}, null, 2)}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Subscription ID:</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{data.id || 'N/A'}</Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Status:</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{attributes.status || 'N/A'}</Typography>
      </Box>
    );
  };

  return (
    <>
      <SharedAppBar title="Webhook Log" />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleNavigateHome}
          >
            Back to Admin
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Webhook Log
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and retry LemonSqueezy webhook events
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>

          {/* Event Type Filters */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Event Type</Typography>
            <FormGroup row>
              {EVENT_TYPES.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedEventTypes.includes(type)}
                      onChange={() => handleEventTypeToggle(type)}
                    />
                  }
                  label={type}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Status Filters */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Status</Typography>
            <FormGroup row>
              {STATUSES.map(status => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                    />
                  }
                  label={status}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Date Range Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue as Dayjs | null);
                  setPage(0);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue as Dayjs | null);
                  setPage(0);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </LocalizationProvider>
          </Box>

          {/* Family ID Filter */}
          <Box>
            <TextField
              label="Family ID"
              value={familyId}
              onChange={(e) => {
                setFamilyId(e.target.value);
                setPage(0);
              }}
              size="small"
              type="number"
              sx={{ width: 200 }}
            />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <Paper>
          {loading && webhooks.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Event Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Family</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Retries</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhooks.map((webhook) => (
                      <React.Fragment key={webhook.id}>
                        <TableRow hover onClick={() => handleExpandRow(webhook.id)} sx={{ cursor: 'pointer' }}>
                          <TableCell>
                            <IconButton size="small">
                              {expandedRow === webhook.id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{formatDate(webhook.createdAt)}</TableCell>
                          <TableCell>{webhook.eventType}</TableCell>
                          <TableCell>
                            <Chip
                              label={webhook.status}
                              color={getStatusColor(webhook.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {webhook.familyId && webhook.familyName ? (
                              <Link
                                href={`/admin/families/${webhook.familyId}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/admin/families/${webhook.familyId}`);
                                }}
                                underline="hover"
                              >
                                {webhook.familyName}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {webhook.errorMessage ? (
                              <Tooltip title={webhook.errorMessage}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {webhook.errorMessage}
                                </Typography>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {webhook.retryCount > 0 ? (
                              <Chip label={webhook.retryCount} size="small" color="warning" />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {webhook.status === 'failed' && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Replay />}
                                onClick={() => handleRetryClick(webhook)}
                              >
                                Retry
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={expandedRow === webhook.id} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Tabs value={tabValue} onChange={handleTabChange}>
                                  <Tab label="Formatted View" />
                                  <Tab label="Raw JSON" />
                                </Tabs>
                                <TabPanel value={tabValue} index={0}>
                                  {renderFormattedPayload(webhook.payload)}
                                </TabPanel>
                                <TabPanel value={tabValue} index={1}>
                                  <Box
                                    component="pre"
                                    sx={{
                                      backgroundColor: theme.palette.grey[100],
                                      p: 2,
                                      borderRadius: 1,
                                      overflow: 'auto',
                                      maxHeight: 400
                                    }}
                                  >
                                    {JSON.stringify(parsePayload(webhook.payload), null, 2)}
                                  </Box>
                                </TabPanel>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>

        {/* Retry Confirmation Dialog */}
        <Dialog open={retryDialogOpen} onClose={handleRetryCancel}>
          <DialogTitle>Retry Webhook Event?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to retry this webhook event?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRetryCancel} disabled={retrying}>
              Cancel
            </Button>
            <Button
              onClick={handleRetryConfirm}
              variant="contained"
              disabled={retrying}
              startIcon={retrying ? <CircularProgress size={20} /> : undefined}
            >
              {retrying ? 'Retrying...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default AdminWebhookLog;
